import { Injectable } from '@angular/core';
import { models } from '@kpi-intelligence/asset-lifecycle';

import { KPI } from '../api';
import { ScopeService } from './scope.service';

export interface ConsistencyLifeTime {
  isConsistent: boolean;
  maxSuggestedLifetime: number;
  minSuggestedLifetime: number;
}

export interface ConsistencyLifeTimeWithNote extends ConsistencyLifeTime {
  note: number;
  year: number;
}

@Injectable()
export class ConsistencyService {

  private model: models.BaseModel;
  private config: KPI.Config = {
    currency: null,
    notation_alpha_coefficient: 2,
    margin_consistency_technical_state_lifetime: 0.15,
    quote_part_enabled: false,
    investmentFullNomenclature: false
  };

  constructor(
    private scope: ScopeService,
  ) {
    // Init model with alpha = 4
    this.model = new models.StandardModel(this.config.notation_alpha_coefficient);

    this.scope.config$.subscribe((config) => {
      this.config = config;
      this.model = new models.StandardModel(this.config.notation_alpha_coefficient);
    });
  }

  /**
   * Verify if the tuple technical state - lifetime - installation is consistent
   *
   * @param year the audit year
   * @param installationYear the asset installation year
   * @param note the new note
   * @param lifetime the asset lifetime
   * @param inputLifetime the new lifetime
   */
  public areTechnicalStateAndLifetimeConsistent(
    year: number,
    installationYear: number,
    note: number,
    lifetime: number,
    inputLifetime: number,
    alphaCoefficient?: number,
  ): ConsistencyLifeTimeWithNote {
    if (alphaCoefficient) {
      this.model = new models.StandardModel(alphaCoefficient);
    }

    const estimatedLifetime = this.model.estimatedLifetime(
      year,
      note,
      installationYear,
      installationYear,
      100,
      lifetime,
      0,
    );

    let maxSuggestedLifetime: number = null;
    let minSuggestedLifetime: number = null;
    let isConsistent = true;

    // If the lifetime is NaN (equivalent to infinite value in the model) we stop the consistency test
    if (!Number.isNaN(estimatedLifetime)) {

      // add the margin to get the max suggested age
      maxSuggestedLifetime = Math.round(
        (1 + <number>this.config.margin_consistency_technical_state_lifetime) * estimatedLifetime,
      );

      // subtract the margin to get the min suggested age
      // butdon't let the minimum suggested age be less than the current age
      minSuggestedLifetime = Math.max(
        Math.max(0, year - installationYear),
        Math.round(
          (1 - <number>this.config.margin_consistency_technical_state_lifetime) * estimatedLifetime,
        ),
      );

      isConsistent = maxSuggestedLifetime >= inputLifetime && minSuggestedLifetime <= inputLifetime;
    }

    return {
      isConsistent,
      note,
      year,
      maxSuggestedLifetime,
      minSuggestedLifetime,
    };
  }

  public getEstimatedRemainingLifetime(
    year: number,
    installationYear: number,
    note: number,
    lifetime: number,
    alphaCoefficient?: number,
  ): number {
    if (alphaCoefficient) {
      this.model = new models.StandardModel(alphaCoefficient);
    }

    const value = this.model.estimatedLifetime(
      year,
      note,
      installationYear,
      installationYear,
      100,
      lifetime,
      0,
    );
    const assetAge = year - installationYear;
    return Math.round(value - assetAge);
  }

}
